import { isDefined } from 'app-shared/utils';
import { useStore } from 'jotai';
import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { HOTKEY_SEQUENCE_TIMEOUT_IN_MILLISECONDS } from '@/ui/utilities/hotkey/constants/HotkeySequenceTimeoutInMilliseconds';
import { PAGE_HOTKEY_LAYERS } from '@/ui/utilities/hotkey/constants/PageHotkeyLayers';
import { openHotkeyLayersState } from '@/ui/utilities/hotkey/states/openHotkeyLayersState';
import { type HotkeyLayer } from '@/ui/utilities/hotkey/types/HotkeyLayer';
import { getTopHotkeyLayer } from '@/ui/utilities/hotkey/utils/getTopHotkeyLayer';

type UseGlobalHotkeysArgs = {
  // A combination such as 'mod+k' or 'escape' ('mod' is Cmd on macOS and
  // Ctrl elsewhere), or a two-key sequence separated by a space, such as 'g h'.
  keys: string;
  callback: () => void;
  // The shortcut only fires while the top-most open layer is one of these.
  activeOnLayers?: readonly HotkeyLayer[];
  // Off by default so typing in a field never triggers a shortcut. Only
  // combinations with a modifier should opt in.
  enableOnFormTags?: boolean;
};

// The only place allowed to call useHotkeys: every shortcut then gets the same
// layer scoping, the same text-field rule and sequence support.
export const useGlobalHotkeys = ({
  keys,
  callback,
  activeOnLayers = PAGE_HOTKEY_LAYERS,
  enableOnFormTags = false,
}: UseGlobalHotkeysArgs) => {
  const store = useStore();
  const [firstKey, secondKey] = keys.split(' ');
  const isSequence = isDefined(secondKey);
  const [sequenceStartTimestamp, setSequenceStartTimestamp] = useState<
    number | null
  >(null);

  // Ignoring the event, rather than disabling the hotkey, leaves it untouched
  // for the layer that owns it (react-hotkeys-hook stops the propagation of
  // events that reach a disabled hotkey).
  const isOutsideActiveLayers = useCallback(
    () =>
      !activeOnLayers.includes(
        getTopHotkeyLayer(store.get(openHotkeyLayersState.atom)),
      ),
    [store, activeOnLayers],
  );

  const handleSequenceKeyDown = (event: KeyboardEvent) => {
    const pressedKey = event.key.toLowerCase();
    const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
    const isSecondKeyInTime =
      sequenceStartTimestamp !== null &&
      event.timeStamp - sequenceStartTimestamp <=
        HOTKEY_SEQUENCE_TIMEOUT_IN_MILLISECONDS;

    if (!hasModifier && isSecondKeyInTime && pressedKey === secondKey) {
      event.preventDefault();
      setSequenceStartTimestamp(null);
      callback();
      return;
    }

    setSequenceStartTimestamp(
      !hasModifier && pressedKey === firstKey ? event.timeStamp : null,
    );
  };

  useHotkeys(
    isSequence ? '*' : keys,
    (event) => {
      if (isSequence) {
        handleSequenceKeyDown(event);
        return;
      }

      callback();
    },
    {
      enableOnFormTags,
      ignoreEventWhen: isOutsideActiveLayers,
      // A sequence listens to every key, so it can only prevent the default
      // action of its own last key.
      preventDefault: !isSequence,
    },
  );
};
